import React, { useCallback, useMemo, useState } from 'react';
import { Tabs, Badge } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Buckets } from 'ontraccr-common';
import { Trie } from 'ontraccr-common';

import {
  getProjectGroupNotes,
  addProjectGroupNote,
  updateProjectGroup,
  markProjectGroupTagsAsRead,
} from '../projects/state/projects.actions';
import {
  addBucketNote,
  getBucketNotes,
  updateBucket,
  getMyBucketTags,
  markBucketTagsAsRead,
} from './state/buckets.actions';
import Notes from '../notes/Notes';
import ProjectList from '../common/projects/ProjectList';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import { getIdMap, isNullOrUndefined } from '../helpers/helpers';
import FilteredFormList from '../forms/FilteredFormList';
import BucketDetailView from './BucketDetailView';
import { bucketLinkTypeToDisplayName, formatBucketPayload, getMaxAllowed } from './bucketHelpers';
import BucketList from './BucketList';
import Permissions from '../auth/Permissions';

const { TabPane } = Tabs;

const typeToActionMap = {
  ProjectGroups: {
    getNotes: getProjectGroupNotes,
    addNote: addProjectGroupNote,
    updateItem: updateProjectGroup,
  },
  Buckets: {
    getNotes: getBucketNotes,
    addNote: addBucketNote,
    updateItem: updateBucket,
  },
};

export default function BucketAddView({
  formProps = {},
  formRef,
  isAdd,
  hasWritePerms,
  type,
  bucketTemplate,
  errors,
  customFields,
  activeTab,
}) {
  const { t } = useTranslation();
  const {
    id,
    editing = false,
  } = formProps;

  const {
    id: templateId,
    upstreamTypes,
    downstreamTypes,
  } = bucketTemplate;

  const isDisplay = !(isAdd || editing);
  const canReadBucketForms = Permissions.has('BUCKETS_READ_FORMS');
  const dispatch = useDispatch();

  const projectGroups = useSelector((state) => state.projects.projectGroups);
  const buckets = useSelector((state) => state.buckets.buckets);
  const projectGroupNotes = useSelector((state) => state.projects.projectGroupsNotes);
  const bucketNotes = useSelector((state) => state.buckets.bucketNotes);
  const projects = useSelector((state) => state.projects.projects);
  const bucketTemplates = useSelector((state) => state.buckets.bucketTemplates);
  const equipment = useSelector((state) => state.equipment.equipment);
  const users = useSelector((state) => state.users.users);
  const divisions = useSelector((state) => state.settings.divisions);
  const company = useSelector((state) => state.settings.company);
  const myProjectGroupTags = useSelector((state) => state.projects.myProjectGroupTags);
  const myBucketTags = useSelector((state) => state.buckets.myBucketTags);

  const projectMap = useMemo(() => getIdMap(projects), [projects]);
  const bucketTemplateMap = useMemo(() => getIdMap(bucketTemplates), [bucketTemplates]);
  const bucketMap = useMemo(() => getIdMap(buckets), [buckets]);
  const equipmentMap = useMemo(() => getIdMap(equipment), [equipment]);

  const unreadNoteCount = useMemo(() => {
    if (!id) return 0;

    if (type === 'ProjectGroups' && myProjectGroupTags) {
      return myProjectGroupTags.filter((tag) => tag.projectGroupId === id).length;
    }
    if (type === 'Buckets' && myBucketTags) {
      return myBucketTags.filter((tag) => tag.bucketId === id).length;
    }

    return 0;
  }, [type, id, myProjectGroupTags, myBucketTags]);

  const [currentActiveTab, setCurrentActiveTab] = useState(activeTab || 'details');

  const unreadNoteIds = useMemo(() => {
    if (!id) return new Set();

    if (type === 'ProjectGroups' && myProjectGroupTags) {
      return new Set(
        myProjectGroupTags
          .filter((tag) => tag.projectGroupId === id)
          .map((tag) => tag.projectGroupNoteId),
      );
    }
    if (type === 'Buckets' && myBucketTags) {
      return new Set(
        myBucketTags
          .filter((tag) => tag.bucketId === id)
          .map((tag) => tag.bucketNoteId),
      );
    }

    return new Set();
  }, [type, id, myProjectGroupTags, myBucketTags]);

  // Handle tab changes - mark tags as read when notes tab is viewed
  const onTabChange = useCallback((activeKey) => {
    setCurrentActiveTab(activeKey);

    if (activeKey === 'notes' && id) {
      if (type === 'ProjectGroups' && myProjectGroupTags) {
        const unreadNoteIds = new Set(
          myProjectGroupTags
            .filter((tag) => tag.projectGroupId === id)
            .map((tag) => tag.projectGroupNoteId),
        );
        if (unreadNoteIds.size > 0) {
          dispatch(markProjectGroupTagsAsRead(Array.from(unreadNoteIds)));
        }
      } else if (type === 'Buckets' && myBucketTags) {
        const unreadTags = myBucketTags.filter((tag) => tag.bucketId === id);
        if (unreadTags.length > 0) {
          const noteIds = unreadTags.map((tag) => tag.bucketNoteId);
          dispatch(markBucketTagsAsRead(noteIds));
        }
      }
    }
  }, [type, id, myProjectGroupTags, myBucketTags, dispatch]);

  const {
    relevantDataSource,
    relevantNotes,
    relevantName,
  } = useMemo(() => {
    switch (type) {
      case 'ProjectGroups':
        return {
          relevantDataSource: projectGroups,
          relevantNotes: projectGroupNotes,
          relevantName: 'Group',
          relevantProjectDownstreamKey: 'downstreamIds',
        };
      case 'Buckets':
        return {
          relevantDataSource: buckets,
          relevantNotes: bucketNotes,
          relevantName: null,
        };
      default:
        return {
          relevantDataSource: [],
          relevantNotes: {},
          relevantName: '',
        };
    }
  }, [
    type,
    buckets,
    projectGroups,
    bucketNotes,
    projectGroupNotes,
  ]);

  const {
    name,
    number,
    description,
    overrideName,
    overrideLabel,
    bucketTemplateId,
    upstreamIdMap,
    downstreamIds,
    downstreamIdMap,
    selectedProjectIds,
    statusId,
  } = useMemo(() => {
    const relevantItem = relevantDataSource.find((pg) => pg.id === id) || {};
    const relevantItemUpstreamIdMap = {};
    const relevantItemDownstreamIdMap = {};
    const projectIds = [];

    upstreamTypes?.forEach((upstreamType) => {
      relevantItemUpstreamIdMap[upstreamType] = relevantItem[upstreamType] ?? [];
    });

    downstreamTypes?.forEach((downstreamType) => {
      if (downstreamType.includes('project')) {
        projectIds.push(...(relevantItem[downstreamType] ?? []));
      }

      relevantItemDownstreamIdMap[downstreamType] = relevantItem[downstreamType] ?? [];
    });

    // For project groups as they do not have a downstream type
    if (relevantItem.downstreamIds) {
      relevantItemDownstreamIdMap.downstreamIds = relevantItem.downstreamIds;
      projectIds.push(...relevantItem.downstreamIds);
    }

    return {
      ...relevantItem,
      upstreamIdMap: relevantItemUpstreamIdMap,
      downstreamIdMap: relevantItemDownstreamIdMap,
      downstreamIds: Object.values(relevantItemDownstreamIdMap).flat(),
      selectedProjectIds: projectIds,
    };
  }, [relevantDataSource, upstreamTypes, downstreamTypes]);

  // Prepare user data for tagging functionality
  const {
    userTrie = {},
    bucketUsers = [],
  } = useMemo(() => {
    const { userId: ownerId } = company || {};
    const fullUsers = [];
    const fullUserTrie = {};

    // Get division IDs from linked projects
    const relevantDivisionIds = new Set();

    if (selectedProjectIds?.length > 0) {
      // Get division IDs from linked projects
      selectedProjectIds.forEach((projectId) => {
        const project = projectMap[projectId];
        if (project?.divisionId) {
          relevantDivisionIds.add(project.divisionId);
        }
      });
    } else {
      Object.keys(divisions).forEach((divisionId) => {
        relevantDivisionIds.add(divisionId);
      });
    }
    // Get users from all relevant divisions
    const divUsers = new Set();
    relevantDivisionIds.forEach((divisionId) => {
      const { users: divisionUsers = new Set() } = divisions[divisionId] ?? {};
      divisionUsers.forEach((userId) => divUsers.add(userId));
    });

    users.forEach((user) => {
      if (user.active && (divUsers.has(user.id) || user.id === ownerId)) {
        fullUsers.push(user);
        Trie.addToTrie(fullUserTrie, user);
      }
    });

    return {
      bucketUsers: fullUsers,
      userTrie: fullUserTrie,
    };
  }, [users, company, divisions, selectedProjectIds, projectMap]);

  const checkDownstreamForType = useCallback(({
    downstreamTypes: relevantDownstreamTypes,
    type: relevantDownstreamType,
    seenTemplates = new Set(),
  }) => {
    const hasRelevantTypeDownstream = relevantDownstreamTypes.some((downstreamType) => (
      downstreamType?.includes(relevantDownstreamType)
    ));

    if (hasRelevantTypeDownstream) return hasRelevantTypeDownstream;

    const downstreamTypesToCheck = [];
    relevantDownstreamTypes?.forEach((downstreamType) => {
      const relevantBucketTemplate = bucketTemplateMap?.[
        Buckets.getTemplateIdFromType(downstreamType)
      ];

      if (relevantBucketTemplate && !seenTemplates.has(relevantBucketTemplate)) {
        downstreamTypesToCheck.push(...(relevantBucketTemplate.downstreamTypes || []));
        seenTemplates.add(relevantBucketTemplate);
      }
    });

    if (!downstreamTypesToCheck?.length) return false;

    return checkDownstreamForType({
      downstreamTypes: downstreamTypesToCheck,
      type: relevantDownstreamType,
      seenTemplates,
    });
  }, [
    bucketTemplateMap,
  ]);

  const hasEquipmentDownstream = useMemo(() => {
    if (!downstreamTypes) return false;
    return checkDownstreamForType({
      downstreamTypes,
      type: 'equipment',
    });
  }, [downstreamTypes, checkDownstreamForType]);

  const detailBody = useMemo(() => (
    <BucketDetailView
      id={id}
      templateId={templateId}
      name={name}
      number={number}
      description={description}
      overrideName={overrideName}
      overrideLabel={overrideLabel}
      upstreamTypes={upstreamTypes}
      upstreamIdMap={upstreamIdMap}
      downstreamIds={downstreamIds}
      isDisplay={isDisplay}
      isAdd={isAdd}
      formRef={formRef}
      projectMap={projectMap}
      bucketTemplateMap={bucketTemplateMap}
      errors={errors}
      customFields={customFields}
      hasEquipmentDownstream={hasEquipmentDownstream}
      statusId={statusId}
    />
  ), [
    templateId,
    id,
    name,
    number,
    description,
    overrideName,
    overrideLabel,
    upstreamTypes,
    upstreamIdMap,
    downstreamIds,
    isDisplay,
    isAdd,
    formRef,
    projectMap,
    bucketTemplateMap,
    errors,
    customFields,
    hasEquipmentDownstream,
    statusId,
  ]);

  const onAddNew = useCallback((downstreamKey = 'downstreamIds') => async (newChild) => dispatch(
    typeToActionMap[type].updateItem(id, formatBucketPayload({
      [downstreamKey]: [
        ...downstreamIds,
        newChild.id,
      ],
      bucketTemplateId,
    })),
  ), [downstreamIds, type, bucketTemplateId]);

  const onAddExistingDownstream = useCallback((downstreamKey = 'downstreamIds') => async (newDownstreamIds) => dispatch(
    typeToActionMap[type].updateItem(id, formatBucketPayload({
      ...downstreamIdMap,
      [downstreamKey]: [
        ...(downstreamIdMap[downstreamKey] ?? []),
        ...newDownstreamIds,
      ],
      bucketTemplateId,
    })),
  ), [
    type,
    bucketTemplateId,
    downstreamIdMap,
  ]);

  const onRemove = useCallback((downstreamKey = 'downstreamIds') => async (
    relevantId,
    removeType = 'project',
  ) => new Promise((resolve) => {
    let relevantMap = {};
    let relevantType = '';

    let formattedRemoveType = removeType;
    if (downstreamKey?.includes('equipment')) {
      formattedRemoveType = 'equipment';
    }

    switch (formattedRemoveType) {
      case 'project':
        relevantMap = projectMap;
        relevantType = t('Project');
        break;
      case 'equipment':
        relevantMap = equipmentMap;
        relevantType = 'Equipment';
        break;
      case 'bucket':
        relevantMap = bucketMap;
        relevantType = 'Bucket';
        break;
      default:
        break;
    }

    const relevantEntity = relevantMap[relevantId];
    CustomConfirmModal({
      title: `Remove ${relevantType} ${relevantEntity?.name} from ${relevantName ?? name}`,
      okText: 'Remove',
      cancelText: 'Cancel',
      onCancel() {
        resolve();
      },
      onOk() {
        const newDownstreamIds = (downstreamIdMap[downstreamKey] ?? [])
          .filter((downstreamId) => downstreamId !== relevantId);
        dispatch(typeToActionMap[type].updateItem(id, formatBucketPayload({
          ...downstreamIdMap,
          [downstreamKey]: [
            ...newDownstreamIds,
          ],
          bucketTemplateId,
        })));
        resolve(true);
      },
    });
  }), [
    projectMap,
    name,
    relevantName,
    type,
    bucketTemplateId,
    downstreamIdMap,
    bucketMap,
    equipmentMap,
  ]);

  const projectMaxAllowed = getMaxAllowed({ entityType: type, downstreamTypes });
  const isProjectReadOnly = (
    !hasWritePerms
    || (
      !isNullOrUndefined(projectMaxAllowed)
      && projectMaxAllowed <= selectedProjectIds?.length
    )
  );

  const ProjectTabPane = useCallback(({ downstreamKey }) => (
    <div className="project-group-project-list">
      <ProjectList
        name={name}
        isDisplay={isProjectReadOnly}
        onAddExistingProjects={onAddExistingDownstream(downstreamKey)}
        selectedProjectIds={selectedProjectIds}
        type="ProjectGroup"
        onAddNew={onAddNew(downstreamKey)}
        onRemove={onRemove(downstreamKey)}
        maxAllowed={projectMaxAllowed}
      />
    </div>
  ), [
    name,
    isProjectReadOnly,
    onAddExistingDownstream,
    selectedProjectIds,
    onAddNew,
    onRemove,
    projectMaxAllowed,
  ]);

  if (!isDisplay) return detailBody;

  return (
    <Tabs className="user-add-form" onChange={onTabChange} activeKey={currentActiveTab}>
      <TabPane tab="Details" key="details">
        {detailBody}
      </TabPane>
      { type === 'ProjectGroups' && (
        <TabPane tab={t('Project', { count: 2 })} key="projects">
          <ProjectTabPane />
        </TabPane>
      )}
      { downstreamTypes?.map((downstreamType) => (
        <TabPane
          tab={bucketLinkTypeToDisplayName(t, downstreamType, bucketTemplateMap)}
          key={downstreamType}
        >
          { Buckets.bucketLinkTypeToEntity(downstreamType) === 'projects'
            ? (
              <ProjectTabPane downstreamKey={downstreamType} />
            ) : (
              <BucketList
                currentTemplateId={templateId}
                bucketTemplate={bucketTemplateMap[Buckets.getTemplateIdFromType(downstreamType)]}
                selectedBucketIds={downstreamIdMap[downstreamType] ?? []}
                downstreamKey={downstreamType}
                onAddExistingBucket={onAddExistingDownstream(downstreamType)}
                onRemove={onRemove(downstreamType)}
              />
            )}
        </TabPane>
      ))}
      <TabPane
        tab={(
          <div style={{ paddingRight: unreadNoteCount ? 15 : 0 }}>
            <Badge count={unreadNoteCount} offset={[5, -5]}>
              Notes
            </Badge>
          </div>
        )}
        key="notes"
      >
        <Notes
          id={id}
          notes={relevantNotes}
          addNote={typeToActionMap[type].addNote}
          getNotes={typeToActionMap[type].getNotes}
          headerStyle={{ paddingLeft: 24 }}
          canSend={hasWritePerms}
          users={bucketUsers}
          userTrie={userTrie}
          allowTags
          unreadSet={unreadNoteIds}
        />
      </TabPane>
      {canReadBucketForms && (
        <TabPane tab="Forms" key="forms">
          <FilteredFormList
            filterId={id}
            filterIds={downstreamIds}
            filterType={type}
            isNotDisplay={false}
            visible
          />
        </TabPane>
      )}
    </Tabs>
  );
}

BucketAddView.propTypes = {
  formProps: PropTypes.shape({}),
  formRef: PropTypes.shape({}),
  isAdd: PropTypes.bool,
  hasWritePerms: PropTypes.bool,
  type: PropTypes.string,
  bucketTemplate: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    upstreamTypes: PropTypes.arrayOf(PropTypes.string),
    downstreamTypes: PropTypes.arrayOf(PropTypes.string),
  }),
  errors: PropTypes.shape({}).isRequired,
  customFields: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

BucketAddView.defaultProps = {
  formProps: {},
  formRef: {},
  isAdd: false,
  hasWritePerms: false,
  type: null,
  bucketTemplate: {},
};
